/**
 * [LGP 2026-10-15, user] NEW FILE — Lead Generator Performance → Leads Count: the Apps Script page "Leads Count"
 * (Index.html data-page="leads-count"; JavaScript.html loadReport, setMarket, setTiles, setLeadsStatus, sortBy, renderRows,
 * patchLeadsPerson, patchLeadsBonus). Same rules: the people of the picked side of the US | ROW switch (Active only — the
 * others are counted in the status line), grouped by team (team order from the server, the team's owner first), a Total under
 * each team and the Grand Total for the whole side (not the search), search by name or email, header sort.
 * [LGP-SLIDE 2026-10-15, user] "when i click us and row it should show left right animation": each side keeps its own table
 * and the switch slides them — US on the left, ROW on the right — like this tab's view switch and Lead Management (a plain
 * sideways slide, no fade, no row wave: the app's toggles are quiet). The tiles and the switch stay where they are.
 * [LGP-DUP 2026-10-16, user] "after the nal kpi, i want to add the duplicate kpi and it will have the number from the leads data
 * lead status column … in the table also after nal column add a column for the duplicate … just like it is working in the team
 * sameer report tab" + "final count will be leads minus nal and minus duplicate": a red Duplicate Leads tile after NAL Leads and
 * a red Duplicate column after NAL (a 0 blank, as NAL); Final Count = Leads − NAL − Duplicate and the Bonus follow (server).
 * [LGP-OVERALL 2026-10-16, user] "on the right of search bar we have us and row toggle but i want to add a part for overall also
 * before the us one, which will show the complete data for the team wise including us and row": the switch is Overall | US |
 * ROW (Monthly Performance's [LGP-SIDES]). Overall = every Active person of both sides by team (a team's US and ROW people
 * together), a Total under each team and the Grand Total = US + ROW; there each name carries its (US) / (ROW) tag and a search
 * for "row" / "us" finds that side's people, as Monthly's Overall. The tiles and the status line follow the view on show; the
 * three tables slide (Overall left, then US, then ROW). Overall the first time, then each browser's last pick.
 * [LGP-CYCLE 2026-10-27, user] "make the start end date same like we have in the team sameer report" + "no need for apply button
 * simply select the date and data will get change according to that": the dates open on the 21st-to-20th work month that holds
 * today (lgpShared defaultRange) and a picked date loads at once — no Apply button; Start max = End, End min = Start, End up to
 * the end of next month (the Team Sameer Report's limits).
 * [LGP-NO-DUP 2026-10-27, user] "the duplicate was never gone to the lead row table. And whatever the count we have in the date column,
 * that's the number of leads we have in the leads row. So … we should only show the date column then the nal minus and then the final
 * count": the Duplicate tile and column are gone — Leads − NAL = Final Count (server; = the Team Sameer Report's Final), the Bonus
 * follows it. A person's duplicates show on the Team Sameer Report.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import DataTable from '../../components/DataTable';
import SingleDateCalendar from '../../components/SingleDateCalendar';
import { SkeletonBox } from '../../components/Skeleton';
import { SlidePane, slidePos } from '../../components/ViewSlide';
import { fetchLgpLeads, LgpLeadsReport, LgpLeadsRow, LgpMarket } from '../../services/api';
import { Column, SortConfig } from '../../types';
import {
  ViewWrap, ViewBar, Field, Toolbar, SearchInput, StatusLine, TableScroll, SideViewport, Muted, NalNum, Tiles, Tile, Seg, TeamHead,
  MIN_DATE, rangeLabel, fmtBonus, ownerFirst, bonusFor, errorText, textCompare, lineBg, defaultRange, lastDayOffered, ViewProps, PersonPatch, dark,
} from './lgpShared';

type Side = 'all' | 'us' | 'row';
const MARKET_LABEL: Record<Side, string> = { all: 'Overall', us: 'US', row: 'ROW' };
/** The switch's order: [LGP-OVERALL] Overall, US, ROW — left to right, the way the tables slide. */
const SIDES: readonly Side[] = ['all', 'us', 'row'];
const SIDE_OPTIONS: { key: Side; label: string }[] = SIDES.map((key) => ({ key, label: MARKET_LABEL[key] }));
/** [LGP-OVERALL] Overall's (US) / (ROW) after a name — Monthly Performance's tag (blue US, violet ROW). */
const SideTag = styled.span<{ $row: boolean }>`
  margin-left: 4px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${(p) => (p.$row ? (dark(p) ? '#c4b5fd' : '#6d28d9') : (dark(p) ? '#93c5fd' : '#1d4ed8'))};
`;
type Line = { id: string; kind?: 'head' | 'sub' | 'grand'; team: string; name: string; email: string; total: number | null; nal: number | null; final: number | null; bonus: number | null; count?: number;
  /** [LGP-OVERALL] the person's side, shown after the name in Overall only */
  side?: 'US' | 'ROW' };
type SortKey = 'name' | 'email' | 'total' | 'nal' | 'final' | 'bonus';
type Sums = { total: number; nal: number; final: number; bonus: number };

/** [JavaScript.html sumRows] ([LGP-NO-DUP] no duplicate any more) */
function sumRows(list: LgpLeadsRow[]): Sums {
  return list.reduce((acc, r) => {
    acc.total += r.total; acc.nal += r.nal; acc.final += r.final; acc.bonus += (r.bonus || 0);
    return acc;
  }, { total: 0, nal: 0, final: 0, bonus: 0 });
}

/** [JavaScript.html renderRows] One side's table lines: its people (the search applied), grouped by team, a Total under each
 *  team, the Grand Total for the whole side last. [LGP-OVERALL] tagged = Overall: each name with its (US) / (ROW) tag, and the
 *  search also looks at "name (side)" — "row" lists the ROW people (Monthly Performance's Overall search). */
const sideTag = (r: LgpLeadsRow): 'US' | 'ROW' => (r.market === 'row' ? 'ROW' : 'US');
function buildLines(teams: string[], base: LgpLeadsRow[], sums: Sums, search: string, sortKey: SortKey, sortAsc: boolean, tagged = false): Line[] {
  const q = search.trim().toLowerCase();
  const rows = base.filter((r) => !q || (tagged ? `${r.name} (${sideTag(r)})` : r.name).toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
  rows.sort((a, b) => {
    // Blank names always sink to the bottom, whichever way the column is sorted.
    if (sortKey === 'name' && !a.name !== !b.name) return a.name ? -1 : 1;
    const x = a[sortKey]; const y = b[sortKey];
    let cmp = typeof x === 'string' ? textCompare(x, y as string) : (x as number) - (y as number);
    if (!cmp) cmp = textCompare(a.name, b.name) || textCompare(a.email, b.email);
    return sortAsc ? cmp : -cmp;
  });
  if (!rows.length) return [];
  const out: Line[] = [];
  for (const team of teams) {
    const members = ownerFirst(rows.filter((r) => r.team === team), team);
    if (!members.length) continue;
    out.push({ id: `head:${team}`, kind: 'head', team, name: team, email: '', total: null, nal: null, final: null, bonus: null, count: members.length });
    for (const r of members) out.push({ id: `p:${r.email.toLowerCase()}`, team, name: r.name, email: r.email, total: r.total, nal: r.nal, final: r.final, bonus: r.bonus, ...(tagged ? { side: sideTag(r) } : {}) });
    const sub = sumRows(members);           // the team's Total reflects the current filter
    out.push({ id: `sub:${team}`, kind: 'sub', team, name: 'Total', email: '', ...sub });
  }
  // The Grand Total closes the table — the whole range for this side, not the search-filtered view.
  out.push({ id: 'grand', kind: 'grand', team: '', name: 'Grand Total', email: `${base.length} ${base.length === 1 ? 'person' : 'people'}`, ...sums });
  return out;
}

const LeadsCountView: React.FC<ViewProps> = ({ active, noDock, storageKey, onVersion, register }) => {
  const defaults = useMemo(() => defaultRange(), []);
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [heading, setHeading] = useState(rangeLabel(defaults.start, defaults.end));
  const [report, setReport] = useState<LgpLeadsReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; error?: boolean } | null>({ text: 'Reading data…' });
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const marketKey = `dt-lgp-market:${storageKey}`;
  // [LGP-OVERALL] Overall the first time (as Monthly Performance), then this browser's last pick
  const [market, setMarketState] = useState<Side>(() => {
    try { const v = localStorage.getItem(marketKey); return v === 'us' || v === 'row' ? v : 'all'; } catch { return 'all'; }
  });
  const req = useRef(0);
  const reportRef = useRef(report);
  reportRef.current = report;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  /** [JavaScript.html loadReport] silent = a background refresh: what is on screen stays until the new numbers land. */
  const load = useCallback((silent: boolean, s?: string, e?: string) => {
    const from = s ?? reportRef.current?.start ?? start;
    const to = e ?? reportRef.current?.end ?? end;
    if (!from || !to) { setNote({ text: 'Pick both dates first.', error: true }); return; }
    if (!silent) {
      setHeading(rangeLabel(from, to));    // the same headings the loaded table will have
      setBusy(true);
      setNote({ text: 'Reading data…' });
      setReport(null);                     // the skeleton, until the new range lands
    }
    const n = ++req.current;
    fetchLgpLeads(from, to)
      .then((data) => {
        if (n !== req.current) return;     // only the newest request may land
        setReport(data);
        setHeading(data.heading);          // the server may have clamped the range
        setBusy(false);
        setNote(null);
        onVersion(data.meta.version, 'leads-count');
      })
      .catch((err) => {
        if (n !== req.current) return;
        if (silent) return;
        setReport(null);
        setBusy(false);
        setNote({ text: errorText(err), error: true });
      });
  }, [start, end, onVersion]);
  const loadRef = useRef(load);
  loadRef.current = load;
  // [LGP-CYCLE 2026-10-27] a picked date loads its numbers at once (no Apply)
  const lastDay = useMemo(() => lastDayOffered(), []);
  const pickStart = (d: string) => { if (d === start) return; setStart(d); load(false, d, end); };
  const pickEnd = (d: string) => { if (d === end) return; setEnd(d); load(false, start, d); };

  /** [JavaScript.html patchLeadsPerson] A roster change applied at once, by the server's own rules (marketOf_). */
  const patchPerson = useCallback((p: PersonPatch) => {
    const cur = reportRef.current;
    if (!cur || busyRef.current || !p || !p.email) return;
    const em = String(p.email).toLowerCase();
    const isActive = /^active$/i.test(String(p.status || '').trim());
    const side: LgpMarket = !isActive ? '' : (/^row$/i.test(String(p.rowStatus || '').trim()) ? 'row' : 'us');
    const team = String(p.leader || '').trim() || 'Not in roster';
    let hit = false;
    const rows = cur.rows.map((r) => {
      if (String(r.email).toLowerCase() !== em) return r;
      hit = true;
      return { ...r, market: side, team, name: String(p.name || '').trim() ? String(p.name).trim() : r.name };
    });
    if (!hit) return;                      // no leads in this range: nothing on the page moves
    const teams = cur.teams.slice();
    if (!teams.includes(team)) {
      const unknown = teams.indexOf('Not in roster');
      if (unknown === -1) teams.push(team); else teams.splice(unknown, 0, team);
    }
    setReport({ ...cur, rows, teams });
  }, []);
  /** [JavaScript.html patchLeadsBonus] New bonus rules applied at once. */
  const patchBonus = useCallback((rules: { from: unknown; to: unknown; amount: unknown }[]) => {
    const cur = reportRef.current;
    if (!cur || !rules) return;
    setReport({ ...cur, rows: cur.rows.map((r) => ({ ...r, bonus: bonusFor(r.final, rules) })) });
  }, []);

  // First opened: load; from then on the page may refresh it and patch it.
  const inited = useRef(false);
  useEffect(() => {
    if (!active || inited.current) return;
    inited.current = true;
    loadRef.current(false);
    register('leads-count', { refresh: () => loadRef.current(true), patchPerson, patchBonus });
  }, [active, register, patchPerson, patchBonus]);

  /** [JavaScript.html setMarket] Instant — both sides are already drawn, the tables slide; remembered per browser. */
  const setMarket = (next: Side) => {
    if (next === market) return;
    setMarketState(next);
    try { localStorage.setItem(marketKey, next); } catch { /* storage blocked */ }
  };

  /** [JavaScript.html sortBy] The same header turns it round; a new one: text A→Z, numbers high→low. */
  const onSort = useCallback((key: string) => {
    const k = key as SortKey;
    if (k === sortKey) setSortAsc((a) => !a);
    else { setSortKey(k); setSortAsc(k === 'name' || k === 'email'); }
  }, [sortKey]);

  // All three views at once (each has its own table for the slide); the tiles and the status line read the view on show.
  // [LGP-OVERALL] Overall = the US and the ROW people together (the inactive / not-in-roster stay out, as on each side).
  const sideRows = useMemo<Record<Side, LgpLeadsRow[]>>(() => ({
    all: report ? report.rows.filter((r) => r.market === 'us' || r.market === 'row') : [],
    us: report ? report.rows.filter((r) => r.market === 'us') : [],
    row: report ? report.rows.filter((r) => r.market === 'row') : [],
  }), [report]);
  const sideSums = useMemo<Record<Side, Sums>>(() => ({ all: sumRows(sideRows.all), us: sumRows(sideRows.us), row: sumRows(sideRows.row) }), [sideRows]);
  const base = sideRows[market];
  const sums = sideSums[market];

  const linesBySide = useMemo<Record<Side, Line[]>>(() => ({
    all: report ? buildLines(report.teams, sideRows.all, sideSums.all, search, sortKey, sortAsc, true) : [],
    us: report ? buildLines(report.teams, sideRows.us, sideSums.us, search, sortKey, sortAsc) : [],
    row: report ? buildLines(report.teams, sideRows.row, sideSums.row, search, sortKey, sortAsc) : [],
  }), [report, sideRows, sideSums, search, sortKey, sortAsc]);

  const emptyBySide = useMemo<Record<Side, { title: string }>>(() => {
    const titleFor = (s: Side) => (!report ? (note?.error ? note.text : 'No leads for this date range.')
      : sideRows[s].length ? 'No one matches that filter.'
        : report.rows.length ? `No ${s === 'all' ? 'active' : MARKET_LABEL[s]} lead generators have leads in this date range.` : 'No leads for this date range.');
    return { all: { title: titleFor('all') }, us: { title: titleFor('us') }, row: { title: titleFor('row') } };
  }, [report, note, sideRows]);

  const isDark = (useTheme() as DefaultTheme).colorScheme === 'dark';
  const rowBg = useCallback((l: Line) => lineBg(l.kind, isDark), [isDark]);
  const columns: Column[] = useMemo(() => [
    { key: 'name', label: 'Name', sortable: true, render: (v: string, l: Line) => (l.kind === 'head' ? <TeamHead team={l.team} count={l.count || 0} /> : l.kind ? <b>{v}</b>
      : <>{v || '—'}{l.side && <SideTag $row={l.side === 'ROW'} data-side-tag={l.side}>({l.side})</SideTag>}</>) }, // [LGP-OVERALL] the tag in Overall
    { key: 'email', label: 'Email', sortable: true, render: (v: string, l: Line) => (l.kind ? <Muted>{v}</Muted> : v) },
    // [LGP-NO-DUP 2026-10-27, user] the date column (Leads_Raw leads) − NAL = Final Count; no Duplicate column any more
    { key: 'total', label: heading, sortable: true, align: 'right', render: (v: number | null, l: Line) => (v === null ? '' : l.kind ? <b>{v}</b> : v) },
    { key: 'nal', label: 'NAL', sortable: true, align: 'right', render: (v: number | null) => (v ? <NalNum>{v}</NalNum> : '') },
    { key: 'final', label: 'Final Count', sortable: true, align: 'right', render: (v: number | null) => (v === null ? '' : <b>{v}</b>) },
    { key: 'bonus', label: 'Bonus', sortable: true, align: 'right', render: (v: number | null, l: Line) => (v === null ? '' : v ? (l.kind ? <b>{fmtBonus(v)}</b> : fmtBonus(v)) : <Muted>—</Muted>) },
  ], [heading]);
  // Kept the same object between switches, so the two (memoised) tables do not draw again when only the side changes.
  const sort = useMemo<SortConfig>(() => ({ sortBy: sortKey, sortOrder: sortAsc ? 'asc' : 'desc' }), [sortKey, sortAsc]);

  // [JavaScript.html setLeadsStatus] counts for the side on show; says how many are on neither side.
  const none = report ? report.rows.filter((r) => !r.market).length : 0;
  const status = note ? note : report ? {
    text: `${base.length} ${base.length === 1 ? 'person' : 'people'} · ${sums.total.toLocaleString('en-US')} leads in range`
      + (none ? ` · ${none} inactive or not in roster, not shown` : '') + ` · data as of ${report.meta.dataAsOf}`,
  } : { text: '' };
  const tile = (v: number) => (report ? v.toLocaleString('en-US') : busy ? <SkeletonBox $w="48px" $h="20px" /> : '—');

  return (
    <ViewWrap data-lgp-view="leads-count">
      <ViewBar>
        <span className="sub">Leads by person</span>
        <div className="controls">
          <Field data-date="start"><span>Start date</span>
            <SingleDateCalendar value={start} min={MIN_DATE} max={end} onChange={pickStart} hint={false} liquid /* [LIQUID-GLASS 2026-11-02] */ /></Field>
          <Field data-date="end"><span>End date</span>
            <SingleDateCalendar value={end} min={start} max={lastDay} onChange={pickEnd} hint={false} liquid /></Field>
        </div>
      </ViewBar>
      <Tiles>
        <Tile data-tile="people"><div className="l">Lead Generators</div><div className="v">{tile(base.length)}</div></Tile>
        {/* [LGP-NO-DUP 2026-10-27] no Duplicate Leads tile any more: Total Leads − NAL Leads = Final Lead Count */}
        <Tile data-tile="leads"><div className="l">Total Leads · {heading}</div><div className="v">{tile(sums.total)}</div></Tile>
        <Tile data-tile="nal"><div className="l">NAL Leads</div><div className="v nal">{tile(sums.nal)}</div></Tile>
        <Tile $main data-tile="final"><div className="l">Final Lead Count</div><div className="v">{tile(sums.final)}</div></Tile>
      </Tiles>
      <Toolbar>
        <div className="left">
          <SearchInput type="search" placeholder="Filter by name or email" value={search} disabled={busy || !report}
            onChange={(e) => setSearch(e.target.value)} />
          <Seg label="Market" value={market} disabled={busy} onPick={setMarket} options={SIDE_OPTIONS} />{/* [LGP-OVERALL] Overall | US | ROW */}
        </div>
        <StatusLine $error={!!status.error} data-status="">{status.text}</StatusLine>
      </Toolbar>
      <SideViewport>
        {SIDES.map((s) => (
          <SlidePane key={s} $pos={slidePos(market, s, SIDES)} $quiet $flat $nested data-market-pane={s} aria-hidden={market !== s}>
            <TableScroll $noDock={noDock}>
              <DataTable
                columns={columns}
                data={linesBySide[s]}
                sort={sort}
                onSort={onSort}
                loading={busy && !report}
                rowBgFn={rowBg}
                disableMobileCards
                parentScroll
                resizeStorageKey={`dt-lgp-leads-colw:${storageKey}`}
                empty={emptyBySide[s]}
              />
            </TableScroll>
          </SlidePane>
        ))}
      </SideViewport>
    </ViewWrap>
  );
};

export default LeadsCountView;
